import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { useEditorState, type Editor } from "@tiptap/react";
import type { SearchResponse, SearchResult } from "@quill/shared";
import { api } from "../api";
import { findStatus, goTo, replaceAll, replaceCurrent, revealMatch, selectMatch, setQuery, step } from "../find";
import { useOnline } from "../hooks";
import { navigate } from "../router";

export interface FindRequest {
  /** Changes on every Ctrl+F / Find tap, so a second press refocuses the open bar. */
  n: number;
  replace: boolean;
}

interface Props {
  docId: string;
  editor: Editor;
  readOnly: boolean;
  request: FindRequest;
  onClose(): void;
}

const numberFormat = new Intl.NumberFormat();
/** Searching a novel on every keystroke is wasted work; wait for a short pause. */
const SEARCH_DELAY_MS = 120;
/** Searching every document asks the server: wait a little longer. */
const ALL_DOCS_DELAY_MS = 250;

/**
 * The search, kept while moving between documents: the bar is rebuilt for each one, and
 * opening a result from "All documents" lands on that match (`jumpTo`, its number in the document).
 */
const memory = { text: "", matchCase: false, wholeWord: false, allDocs: false, jumpTo: null as number | null };

/** Find (and replace) bar, pinned above the status line. */
export function FindBar({ docId, editor, readOnly, request, onClose }: Props) {
  const [text, setText] = useState(memory.text);
  const [replacement, setReplacement] = useState("");
  const [matchCase, setMatchCase] = useState(memory.matchCase);
  const [wholeWord, setWholeWord] = useState(memory.wholeWord);
  const [allDocs, setAllDocs] = useState(memory.allDocs && !request.replace);
  const [showReplace, setShowReplace] = useState(request.replace && !readOnly);
  const [message, setMessage] = useState<string | null>(null);
  const bar = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const view = editor.view;

  const status = useEditorState({
    editor,
    selector: ({ editor: e }) => findStatus(e.state),
    equalityFn: (a, b) =>
      !!b &&
      a.count === b.count && a.index === b.index && a.capped === b.capped && a.current?.from === b.current?.from,
  });

  useEffect(() => {
    Object.assign(memory, { text, matchCase, wholeWord, allDocs });
  }, [text, matchCase, wholeWord, allDocs]);

  // Opened (or asked for again): search for the selected text, if any, and focus the box.
  useLayoutEffect(() => {
    // Arrived from a result: show the text, not the phone keyboard.
    if (memory.jumpTo !== null) return;
    const { from, to, empty } = editor.state.selection;
    const selected = empty ? "" : editor.state.doc.textBetween(from, to, "\n");
    if (selected && selected.length <= 100 && !selected.includes("\n")) setText(selected);
    if (request.replace && !readOnly) {
      setShowReplace(true);
      setAllDocs(false);
    }
    // Before the next key press, so typing straight after Ctrl+F lands in the box.
    input.current?.focus();
    input.current?.select();
  }, [request.n]); // eslint-disable-line react-hooks/exhaustive-deps

  // The search waits for a pause in typing; anything that acts on the matches runs it first.
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);
  const search = useRef(() => {});
  search.current = () => {
    pending.current = null;
    setQuery(view, { text, matchCase, wholeWord });
    if (memory.jumpTo !== null) goTo(view, memory.jumpTo);
    memory.jumpTo = null;
    const { current } = findStatus(view.state);
    if (current) revealMatch(view, current);
  };
  const flush = () => {
    if (!pending.current) return;
    clearTimeout(pending.current);
    search.current();
  };
  useEffect(() => {
    setMessage(null);
    pending.current = setTimeout(() => search.current(), SEARCH_DELAY_MS);
    return () => clearTimeout(pending.current ?? undefined);
  }, [view, text, matchCase, wholeWord]);

  // Clear the highlights when the bar goes away.
  useEffect(() => () => setQuery(view, null), [view]);

  // Let the page scroll its last lines (and the scroll thumb stop) above the bar.
  useEffect(() => {
    const root = document.documentElement;
    const observer = new ResizeObserver(([entry]) => root.style.setProperty("--find-h", `${entry!.borderBoxSize[0]!.blockSize}px`));
    observer.observe(bar.current!);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--find-h");
    };
  }, []);

  const go = (dir: 1 | -1) => {
    flush();
    step(view, dir);
    const { current } = findStatus(view.state);
    if (current) revealMatch(view, current);
  };

  const close = () => {
    flush();
    const { current } = findStatus(view.state);
    if (current) selectMatch(view, current);
    onClose();
    editor.commands.focus();
  };

  const replaceOne = () => {
    flush();
    replaceCurrent(view, replacement);
    const { current } = findStatus(view.state);
    if (current) revealMatch(view, current);
  };

  const replaceEvery = () => {
    flush();
    const n = replaceAll(view, replacement);
    setMessage(n ? `Replaced ${numberFormat.format(n)}` : null);
  };

  /** Opens a result from "All documents" at the chosen match. */
  const open = (result: SearchResult, index: number) => {
    memory.jumpTo = index;
    setAllDocs(false);
    if (result.docId === docId) {
      flush();
      search.current();
    } else {
      memory.allDocs = false;
      navigate(`/d/${result.docId}`);
    }
  };
  const results = useAllDocsSearch(allDocs, text, matchCase, wholeWord);

  const onFindKey = (e: KeyboardEvent) => {
    if (e.key === "Enter" && allDocs) {
      const first = results.response?.results[0];
      if (first) open(first, first.snippets[0]?.index ?? 0);
    } else if (e.key === "Enter") go(e.shiftKey ? -1 : 1);
    else if (e.key === "Escape") close();
    else return;
    e.preventDefault();
  };

  const onReplaceKey = (e: KeyboardEvent) => {
    if (e.key === "Enter") replaceOne();
    else if (e.key === "Escape") close();
    else return;
    e.preventDefault();
  };

  const count = message
    ? message
    : allDocs
      ? results.summary
      : !text
      ? ""
      : status.count
        ? `${numberFormat.format(status.index)} of ${numberFormat.format(status.count)}${status.capped ? "+" : ""}`
        : "No results";

  // Buttons keep focus where it is (the text box), so the phone keyboard stays up.
  const keep = (e: { preventDefault(): void }) => e.preventDefault();

  return (
    <div className="find-bar" ref={bar} role="search" aria-label="Find in document">
      {allDocs && text && <AllResults docId={docId} state={results} onOpen={open} />}
      <div className="find-row">
        <input
          ref={input}
          type="search"
          aria-label="Find"
          placeholder="Find"
          value={text}
          enterKeyHint="search"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onFindKey}
        />
        <span className="find-count" aria-live="polite">
          {count}
        </span>
        {!allDocs && (
          <>
            <button type="button" aria-label="Previous match" title="Previous (Shift+Enter)" disabled={!status.count} onMouseDown={keep} onClick={() => go(-1)}>
              ‹
            </button>
            <button type="button" aria-label="Next match" title="Next (Enter)" disabled={!status.count} onMouseDown={keep} onClick={() => go(1)}>
              ›
            </button>
          </>
        )}
        <button type="button" aria-label="Close find" title="Close (Esc)" onMouseDown={keep} onClick={close}>
          ×
        </button>
      </div>
      <div className="find-row find-options">
        <button type="button" aria-pressed={matchCase} onMouseDown={keep} onClick={() => setMatchCase(!matchCase)}>
          Match case
        </button>
        <button type="button" aria-pressed={wholeWord} onMouseDown={keep} onClick={() => setWholeWord(!wholeWord)}>
          Whole words
        </button>
        <button type="button" aria-pressed={allDocs} onMouseDown={keep} onClick={() => setAllDocs(!allDocs)}>
          All documents
        </button>
        {!readOnly && !allDocs && (
          <button type="button" aria-expanded={showReplace} onMouseDown={keep} onClick={() => setShowReplace(!showReplace)}>
            Replace…
          </button>
        )}
      </div>
      {showReplace && !readOnly && !allDocs && (
        <div className="find-row">
          <input
            type="text"
            aria-label="Replace with"
            placeholder="Replace with"
            value={replacement}
            enterKeyHint="done"
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setReplacement(e.target.value)}
            onKeyDown={onReplaceKey}
          />
          <button type="button" className="find-action" disabled={!status.current} onMouseDown={keep} onClick={replaceOne}>
            Replace
          </button>
          <button
            type="button"
            className="find-action"
            aria-label="Replace all"
            disabled={!status.count}
            onMouseDown={keep}
            onClick={replaceEvery}
          >
            All
          </button>
        </div>
      )}
    </div>
  );
}

interface AllDocsState {
  response: SearchResponse | null;
  /** For the counter: "12 in 3 documents", "Searching…", "No results". */
  summary: string;
  error: string | null;
}

/** Asks the server to search every document, once typing pauses. */
function useAllDocsSearch(enabled: boolean, text: string, matchCase: boolean, wholeWord: boolean): AllDocsState {
  const online = useOnline();
  const [state, setState] = useState<{ key: string; response: SearchResponse | null; error: string | null }>({
    key: "",
    response: null,
    error: null,
  });
  const key = JSON.stringify([text, matchCase, wholeWord]);

  useEffect(() => {
    if (!enabled || !text || !online) return;
    let live = true;
    const timer = setTimeout(() => {
      api
        .search(text, { matchCase, wholeWord })
        .then((response) => live && setState({ key, response, error: null }))
        .catch(() => live && setState({ key, response: null, error: "Couldn't search your documents. Try again in a moment." }));
    }, ALL_DOCS_DELAY_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [enabled, text, matchCase, wholeWord, online, key]);

  if (!enabled || !text) return { response: null, summary: "", error: null };
  if (!online) return { response: null, summary: "Offline", error: "Searching all documents needs a connection." };
  if (state.key !== key) return { response: null, summary: "Searching…", error: null };
  if (state.error) return { response: null, summary: "", error: state.error };
  const results = state.response!.results;
  const total = results.reduce((n, r) => n + r.count, 0);
  const summary = !results.length
    ? "No results"
    : `${numberFormat.format(total)} in ${numberFormat.format(results.length)}${state.response!.truncated ? "+" : ""} ${results.length === 1 ? "doc" : "docs"}`;
  return { response: state.response, summary, error: null };
}

function AllResults({ docId, state, onOpen }: { docId: string; state: AllDocsState; onOpen(r: SearchResult, index: number): void }) {
  if (state.error) return <p className="find-results-note">{state.error}</p>;
  if (!state.response?.results.length) return null;
  return (
    <ul className="find-results" aria-label="Results in all documents">
      {state.response.results.map((r) => (
        <li key={r.docId}>
          <div className="find-result-title">
            <span>{r.title}</span>
            {r.docId === docId && <span className="find-result-here"> · this document</span>}
            <span className="find-result-count">
              {numberFormat.format(r.count)} {r.count === 1 ? "match" : "matches"}
            </span>
          </div>
          {r.snippets.map((s) => (
            <button
              key={s.index}
              type="button"
              className="find-snippet"
              // Keep the phone keyboard down while picking.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onOpen(r, s.index)}
            >
              {s.before}
              <mark>{s.match}</mark>
              {s.after}
            </button>
          ))}
          {r.count > r.snippets.length && (
            <button
              type="button"
              className="find-snippet find-more"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onOpen(r, r.snippets.length)}
            >
              {numberFormat.format(r.count - r.snippets.length)} more in this document…
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}
